import type { Theme } from 'hephaestus/core';
import type { EnrichedPortfolioReport } from '../hooks/usePortfolioTableState.types';
import {
  BAND_CONSUMED_NEGATIVE_THRESHOLD_PERCENT,
  BAND_CONSUMED_WARNING_THRESHOLD_PERCENT,
  DEFAULT_DRIFT_TOLERANCE_PERCENT,
} from './constants';
export type BandConsumedColorTokens = {
  onSurfaceVariant?: string | null;
  warning?: string | null;
  negative?: string | null;
  onDisabled?: string | null;
};

export function buildBandConsumedColorTokens(
  theme: Theme,
): BandConsumedColorTokens {
  return {
    onSurfaceVariant: theme.color.onSurfaceVariant,
    warning: theme.color.warning,
    negative: theme.color.negative,
    onDisabled: theme.color.onDisabled,
  };
}

type BandConsumedMetric = {
  consumedPercentage: number;
  color: string;
  arrowDirection: 'up' | 'down';
};

export function computeBandConsumedMetrics(
  report: EnrichedPortfolioReport,
  driftTolerancePercent: number,
  colorTokens: BandConsumedColorTokens,
): {
  cash: BandConsumedMetric;
  position: BandConsumedMetric | null;
  positionDisabledColor: string;
} {
  const driftPct = report._drift ?? null;
  const positionDisabledColor = getBandConsumedColor(0, colorTokens, true);

  const cashPct =
    report.marketValueCents > 0
      ? ((report.cashAvailableForTradeCents ?? 0) / report.marketValueCents) *
        100
      : 0;
  const cashTarget = (report.expectedCashPercentage ?? 0) * 100;
  const cashOverUnder = cashPct - cashTarget;

  const cashConsumedPercentage = calculateBandConsumedCashPercentage(
    cashOverUnder,
    driftTolerancePercent,
  );
  const cash: BandConsumedMetric = {
    consumedPercentage: cashConsumedPercentage,
    color: getBandConsumedColor(cashConsumedPercentage, colorTokens),
    arrowDirection: getCashArrowDirection(cashOverUnder),
  };

  if (driftPct === null) {
    return { cash, position: null, positionDisabledColor };
  }

  const positionConsumedPercentage = calculateBandConsumedPositionPercentage(
    driftPct,
    driftTolerancePercent,
  );
  const position: BandConsumedMetric = {
    consumedPercentage: positionConsumedPercentage,
    color: getBandConsumedColor(positionConsumedPercentage, colorTokens),
    arrowDirection: getPositionArrowDirection(positionConsumedPercentage),
  };

  return { cash, position, positionDisabledColor };
}

export function calculateBandConsumedPercentage(
  absoluteDeltaPct: number,
  driftTolerancePercent = DEFAULT_DRIFT_TOLERANCE_PERCENT,
): number {
  if (driftTolerancePercent <= 0) return 0;
  return (Math.abs(absoluteDeltaPct) / driftTolerancePercent) * 100;
}

export function calculateBandConsumedCashPercentage(
  cashOverUnder: number,
  driftTolerancePercent = DEFAULT_DRIFT_TOLERANCE_PERCENT,
): number {
  return calculateBandConsumedPercentage(cashOverUnder, driftTolerancePercent);
}

export function calculateBandConsumedPositionPercentage(
  driftPct: number,
  driftTolerancePercent = DEFAULT_DRIFT_TOLERANCE_PERCENT,
): number {
  return calculateBandConsumedPercentage(driftPct, driftTolerancePercent);
}

export function getBandConsumedColor(
  consumedPercentage: number,
  tokens: BandConsumedColorTokens,
  disabled = false,
): string {
  if (disabled) {
    return tokens.onDisabled ?? '';
  }
  if (consumedPercentage >= BAND_CONSUMED_NEGATIVE_THRESHOLD_PERCENT) {
    return tokens.negative ?? '';
  }
  if (consumedPercentage >= BAND_CONSUMED_WARNING_THRESHOLD_PERCENT) {
    return tokens.warning ?? '';
  }
  return tokens.onSurfaceVariant ?? '';
}

export function getBandConsumedBarFill(consumedPercentage: number): number {
  return Math.min(Math.max(consumedPercentage, 0), 100);
}

export function getCashArrowDirection(cashOverUnder: number): 'up' | 'down' {
  return cashOverUnder > 0 ? 'up' : 'down';
}

export function getPositionArrowDirection(
  bandConsumedPositionPercentage: number,
): 'up' | 'down' {
  return bandConsumedPositionPercentage >=
    BAND_CONSUMED_NEGATIVE_THRESHOLD_PERCENT
    ? 'up'
    : 'down';
}
